import { ColorWheelIcon } from '@radix-ui/react-icons'

import { getDocsPath } from 'lib/routes/replace-dynamic-paths'

import { SolidjsIcon } from 'components/atomic/atoms/icons/logos/solidjs'
import BackgroundSquares from 'components/atomic/atoms/svgs/background-squares'
import LayoutWebsite from 'components/pages/@layouts/layout-website'
import { GithubIcon } from 'components/pages/@shared/icons/github'
import { QuickstartIcon } from 'components/pages/@shared/icons/quickstart'
import SharedTemplate from 'components/pages/frameworks/@shared/template'
import { TemplateCodeExampleProps } from 'components/pages/frameworks/@shared/template/template.code-examples'

const framework = 'SolidJS'

const startBuilding = [
  {
    icon: QuickstartIcon,
    title: 'Quickstart',
    text: 'Learn the basics of Grafbase and deploy to production in only a few short steps.',
    href: getDocsPath('/quickstart/get-started'),
    buttonLabel: 'Get Started',
  },
  {
    icon: ColorWheelIcon,
    title: 'Developer Guides',
    text: 'Learn how to use Grafbase with frontend frameworks, external APIs, and more.',
    href: '/guides',
    buttonLabel: 'Read guides',
  },
  {
    icon: GithubIcon,
    title: 'SolidStart Example',
    text: 'See how Grafbase works with SolidJS, SolidStart and TypeScript in this example.',
    external: true,
    href: 'https://github.com/grafbase/grafbase/tree/main/examples/solidstart',
    buttonLabel: 'See Source',
  },
]

const codeExamples: TemplateCodeExampleProps['examples'] = [
  {
    title: 'Data loading',
    description: 'Fetch data on the server inside SolidStart routes.',
    language: 'tsx',
    code: `
import { GraphQLClient, gql } from 'graphql-request'
import { useRouteData } from 'solid-start'
import { createServerData$ } from 'solid-start/server'

export const grafbase = new GraphQLClient('GRAFBASE_API_URL', {
  headers: { 'x-api-key': '...' }
})

const GetAllPosts = gql\`
  {
    postCollection(first: 100) {
      edges {
        node {
          id
          title
          slug
        }
      }
    }
  }
\`

export const routeData = () => {
  return createServerData$(async () => {
    const { postCollection } = await grafbase.request(GetAllPosts)

    return postCollection
  })
}

const App = () => {
  const { edges } = useRouteData<typeof routeData>()

  return (
    <Layout>
      <For each={edges()}>
        {(edge) => !!edge?.node && <Post {...edge.node} />}
      </For>
    </Layout>
  )
}

export default App
`,
  },
  {
    title: 'Server actions',
    description:
      'Handle actions sent from the client with SolidStart server actions.',
    language: 'tsx',
    code: `
import { GraphQLClient, gql } from 'graphql-request'
import { createServerAction$, redirect } from 'solid-start/server'

export const grafbase = new GraphQLClient('GRAFBASE_API_URL', {
  headers: { 'x-api-key': '...' }
})

const CreatePostMutation = gql\`
  mutation CreatePost($title: String!, $slug: String!) {
    postCreate(input: {title: $title, slug: $slug}) {
      post {
        id
      }
    }
  }
\`

const CreatePost = () => {
  const [creatingPost, createPost] = createServerAction$(
    async (form: FormData) => {
      const vars = Object.fromEntries(form)

      const { postCreate } = await grafbase.request(CreatePostMutation, {
        title: vars.title,
        slug: vars.slug
      })

      return redirect(\`/posts/$\{postCreate.post.id}\`)
    }

    return (
      <createPost.Form>
        <input ref={inputRef} name="title" />
        <input ref={slugRef} name="slug" />
        <button type="submit">Create</button>
      </createPost.Form>
    )
  )
}

export default CreatePost
`,
  },
]

const SolidStartJSFramework = () => {
  return (
    <LayoutWebsite
      meta={{ title: framework, imageUrl: '/images/og/solidjs.png' }}
    >
      <SharedTemplate
        hero={{
          subTitle:
            'Build performant, powerful and productive frontends with SolidJS and GraphQL.',
          framework,
          svg: (
            <>
              <SolidjsIcon width={200} height={200} />
              <BackgroundSquares
                absolute
                css={{
                  left: -150,
                  top: -150,
                  zIndex: -1,
                }}
              />
            </>
          ),
        }}
        codeExamples={{ framework, examples: codeExamples }}
        startBuilding={{
          title: 'Build a Solid backend with Grafbase',
          framework,
          columns: startBuilding,
        }}
      />
    </LayoutWebsite>
  )
}

export default SolidStartJSFramework
